import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import * as marketplaceAuthService from '../../services/marketplaceAuthService';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  PhoneAndroid as PhoneIcon, 
  Security as SecurityIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Styled components
const Container = styled(Box)`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  padding: 1rem;
`;

const StyledPaper = styled(Paper)`
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  color: white;
`;

const Title = styled(Typography)`
  margin-bottom: 1rem;
  text-align: center;
  color: white;
`;

const Subtitle = styled(Typography)`
  margin-bottom: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
`;

const StyledStepper = styled(Stepper)`
  margin-bottom: 2rem;
  
  .MuiStepLabel-label {
    color: rgba(255, 255, 255, 0.7);
  }
  
  .MuiStepLabel-active {
    color: white !important;
  }
  
  .MuiStepLabel-completed {
    color: #4caf50 !important;
  }
  
  .MuiStepIcon-root {
    color: rgba(255, 255, 255, 0.3);
  }
  
  .MuiStepIcon-active {
    color: #4a90e2 !important;
  }
  
  .MuiStepIcon-completed {
    color: #4caf50 !important;
  }
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1.5rem;
  width: 100%;
  
  .MuiInputBase-input {
    color: white;
  }
  
  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.7);
  }
  
  .MuiOutlinedInput-root {
    fieldset {
      border-color: rgba(255, 255, 255, 0.3);
    }
    
    &:hover fieldset {
      border-color: rgba(255, 255, 255, 0.5);
    }
    
    &.Mui-focused fieldset {
      border-color: #4a90e2;
    }
  }
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const RecaptchaContainer = styled(Box)`
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
`;

const InfoBox = styled(Box)`
  background: rgba(74, 144, 226, 0.1);
  border-left: 4px solid #4a90e2;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 4px;
`;

// MFA Setup Component
const MFASetup = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  
  const steps = ['Phone Number', 'Verification', 'Complete'];
  
  useEffect(() => {
    // Check if user is logged in
    if (!currentUser) {
      navigate('/marketplace/auth/signin');
      return;
    }
    
    // Initialize recaptcha verifier
    const verifier = marketplaceAuthService.createRecaptchaVerifier('recaptcha-container');
    setRecaptchaVerifier(verifier);
    
    return () => {
      // Clean up recaptcha when component unmounts
      if (verifier) {
        verifier.clear();
      }
    };
  }, [currentUser, navigate]);
  
  const handlePhoneNumberSubmit = async () => {
    if (!phoneNumber) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Format phone number if needed
      const formattedPhoneNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
      
      // Start MFA enrollment
      const result = await marketplaceAuthService.enrollMFA(
        currentUser, 
        formattedPhoneNumber, 
        recaptchaVerifier
      );
      
      setVerificationId(result.verificationId);
      setActiveStep(1);
      setSuccess('Verification code sent to your phone');
    } catch (error) {
      console.error('Error enrolling in MFA:', error);
      setError(error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerificationCodeSubmit = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter a valid verification code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Complete MFA enrollment
      await marketplaceAuthService.completeMFAEnrollment(
        currentUser,
        verificationId,
        verificationCode
      );
      
      setActiveStep(2);
      setSuccess('Two-factor authentication has been successfully set up');
    } catch (error) {
      console.error('Error completing MFA enrollment:', error);
      setError(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisableMFA = async () => {
    setLoading(true);
    setError('');
    
    try {
      await marketplaceAuthService.disableMFA(currentUser);
      setSuccess('Two-factor authentication has been disabled');
      setTimeout(() => {
        navigate('/marketplace/profile');
      }, 2000);
    } catch (error) {
      console.error('Error disabling MFA:', error);
      setError(error.message || 'Failed to disable two-factor authentication');
    } finally {
      setLoading(false);
    }
  };
  
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <InfoBox>
              <Typography variant="body2" color="white">
                <SecurityIcon fontSize="small" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Two-factor authentication adds an extra layer of security to your account.
                After entering your password, you'll need to enter a verification code sent to your phone.
              </Typography>
            </InfoBox>
            
            <StyledTextField
              label="Phone Number"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1234567890"
              helperText="Include country code (e.g., +1 for US)"
              FormHelperTextProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              InputProps={{
                startAdornment: <PhoneIcon style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.7)' }} />,
              }}
              disabled={loading}
            />
            
            <RecaptchaContainer id="recaptcha-container" />
            
            <ButtonContainer>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/marketplace/profile')}
                disabled={loading}
                style={{ color: 'white', borderColor: 'white' }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handlePhoneNumberSubmit}
                disabled={loading || !phoneNumber}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Code'}
              </Button>
            </ButtonContainer>
          </>
        );
        
      case 1:
        return (
          <>
            <InfoBox>
              <Typography variant="body2" color="white">
                <InfoIcon fontSize="small" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Enter the 6-digit verification code sent to {phoneNumber}.
                The code will expire after 10 minutes.
              </Typography>
            </InfoBox>
            
            <StyledTextField
              label="Verification Code"
              variant="outlined"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="123456"
              inputProps={{ maxLength: 6 }}
              disabled={loading}
            />
            
            <ButtonContainer>
              <Button 
                variant="outlined" 
                onClick={() => setActiveStep(0)}
                disabled={loading}
                style={{ color: 'white', borderColor: 'white' }}
              >
                Back
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleVerificationCodeSubmit}
                disabled={loading || !verificationCode || verificationCode.length < 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </ButtonContainer>
          </>
        );
        
      case 2:
        return (
          <>
            <Box display="flex" justifyContent="center" mb={3}>
              <CheckIcon style={{ fontSize: 60, color: '#4caf50' }} />
            </Box>
            
            <Typography variant="h6" align="center" gutterBottom color="white">
              Two-Factor Authentication Enabled
            </Typography>
            
            <Typography variant="body1" align="center" paragraph color="rgba(255, 255, 255, 0.7)">
              Your account is now protected with an additional layer of security.
              You'll need to enter a verification code sent to your phone when signing in.
            </Typography>
            
            <Divider style={{ margin: '1.5rem 0', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Typography variant="body2" color="white" paragraph>
              <strong>Important:</strong> If you lose access to your phone, you may be locked out of your account.
              Consider saving backup codes or adding a backup phone number.
            </Typography>
            
            <ButtonContainer>
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleDisableMFA}
                disabled={loading}
                startIcon={<CloseIcon />}
              >
                Disable 2FA
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/marketplace/profile')}
                disabled={loading}
              >
                Go to Profile
              </Button>
            </ButtonContainer>
          </>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Container>
      <StyledPaper elevation={3}>
        <Title variant="h4">Two-Factor Authentication</Title>
        <Subtitle variant="body1">Secure your account with an additional layer of protection</Subtitle>
        
        <StyledStepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>
        
        {error && (
          <Alert 
            severity="error" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}
        
        {renderStepContent(activeStep)}
      </StyledPaper>
    </Container>
  );
};

export default MFASetup;
